import BoxArrowIcon from "@/assets/colorable/box_arrow.svg?react";
import ChartsIcon from "@/assets/colorable/charts.svg?react";
import CircleWithPlayIcon from "@/assets/colorable/circle_with_play.svg?react";
import RenegotiationTickIcon from "@/assets/colorable/renegotiation_tick.svg?react";
import RenegotiationTimesIcon from "@/assets/colorable/renegotiation_times.svg?react";
import SearchIcon from "@/assets/colorable/search.svg?react";
import SourceContentIcon from "@/assets/colorable/source_content.svg?react";
import UsersIcon from "@/assets/colorable/users.svg?react";
import CrownIcon from "@/assets/dark_gradient/crown.svg?react";
import StarIcon from "@/assets/dark_gradient/star.svg?react";
import UsersGradientIcon from "@/assets/dark_gradient/users.svg?react";
import {settingsModalAtom, tiktokVisibilitySelectedCountryAtom} from "@/store/jotaiAtoms";
import type {TNotificationType} from "@/store/types";
import useUserStore from "@/store/user";
import {getDefaultStore} from "jotai";
import type {ComponentType, SVGProps} from "react";
import * as RPNInput from "react-phone-number-input";

type NotificationParams = {
  BRAND_REVIEW_REMINDER: {campaign_id: number};
  CAMPAIGN_START_REMINDER: {campaign_id: number};
  CAMPAIGN_RENEGOTIATION_ACCEPTED: {campaign_id: number};
  CAMPAIGN_RENEGOTIATION_DECLINED: {campaign_id: number};
  CANCEL_REQUEST: {campaign_id: number};
  EXTENSION_REQUEST: {campaign_id: number};
  NEW_APPLICATIONS: {campaign_id: number};
  NEW_BRAND_AMBASSADOR: {company_id: number; company_slug: string; group_id: number | null};
  NEW_CONTENT_SUBMISSION: {campaign_id: number};
  NEW_PENDING_AMBASSADOR: {company_id: number; company_slug: string; group_id: number | null};
  PERMISSION_UPGRADE_ACCEPTED: {company_id: number};
  PERMISSION_UPGRADE_REJECTED: {company_id: number};
  PERMISSION_UPGRADE_REQUEST: {company_id: number};
  RATING: {campaign_id: number; on_going: boolean};
  REFUND: {campaign_id: number};
  REPORT: {campaign_id: number; on_going: boolean};
  SEND_PACKAGES_REMINDER: {campaign_id: number; on_going: boolean};
  TIKTOK_VISIBILITY_DISCOVERY: {company_id: number; region: string};
};

type NotificationConfig<K extends TNotificationType> = {
  type: K;
  Icon?: ComponentType<SVGProps<SVGSVGElement>>;
  hasGradientBorder?: boolean;
  textClassName: string;
  iconClassName?: string;
  iconTextTranslation: string;
  titleImage?: boolean;
  titleHighlightParam?: string;
  titleHighlightClassName?: string;
  actionUrl?: (params: NotificationParams[K]) => string;
  onClick?: (params: NotificationParams[K]) => void;
};

const NotificationType: {
  [K in TNotificationType]: NotificationConfig<K>;
} = {
  BRAND_REVIEW_REMINDER: {
    type: "BRAND_REVIEW_REMINDER",
    Icon: SourceContentIcon,
    textClassName: "text-warning-dark-yellow",
    iconTextTranslation: "brand_review_reminder_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=active`,
  },
  CAMPAIGN_START_REMINDER: {
    type: "CAMPAIGN_START_REMINDER",
    Icon: CircleWithPlayIcon,
    textClassName: "text-warning-dark-yellow",
    iconTextTranslation: "start_your_campaign_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=overview`,
  },
  CAMPAIGN_RENEGOTIATION_ACCEPTED: {
    type: "CAMPAIGN_RENEGOTIATION_ACCEPTED",
    Icon: RenegotiationTickIcon,
    textClassName: "text-success-herbal",
    iconTextTranslation: "renegotiation_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=applications`,
  },
  CAMPAIGN_RENEGOTIATION_DECLINED: {
    type: "CAMPAIGN_RENEGOTIATION_DECLINED",
    Icon: RenegotiationTimesIcon,
    textClassName: "text-danger-raspberry",
    iconTextTranslation: "renegotiation_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=applications`,
  },
  CANCEL_REQUEST: {
    type: "CANCEL_REQUEST",
    hasGradientBorder: true,
    Icon: UsersGradientIcon,
    iconTextTranslation: "applicant_request",
    textClassName: "text-transparent bg-clip-text bg-linear-(--notification-gradient)",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=preparation`,
  },
  EXTENSION_REQUEST: {
    type: "EXTENSION_REQUEST",
    hasGradientBorder: true,
    Icon: UsersGradientIcon,
    iconTextTranslation: "applicant_request",
    textClassName: "text-transparent bg-clip-text bg-linear-(--notification-gradient)",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=active`,
  },
  NEW_APPLICATIONS: {
    type: "NEW_APPLICATIONS",
    Icon: UsersIcon,
    textClassName: "text-success-herbal",
    iconTextTranslation: "new_influencers_applied_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=overview`,
  },
  NEW_BRAND_AMBASSADOR: {
    type: "NEW_BRAND_AMBASSADOR",
    Icon: CrownIcon,
    hasGradientBorder: true,
    textClassName: "text-transparent bg-clip-text bg-linear-(--notification-gradient)",
    iconClassName: "size-2",
    iconTextTranslation: "new_brand_ambassador_type",
    titleImage: true,
    titleHighlightParam: "first_name",
    titleHighlightClassName: "text-primary-dark-solid",
    actionUrl: params => (params.group_id ? `/group/${params.group_id}/` : `/company/${params.company_id}`),
  },
  NEW_CONTENT_SUBMISSION: {
    type: "NEW_CONTENT_SUBMISSION",
    Icon: SourceContentIcon,
    textClassName: "text-warning-dark-yellow",
    iconTextTranslation: "new_content_submission_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=active`,
  },
  NEW_PENDING_AMBASSADOR: {
    type: "NEW_PENDING_AMBASSADOR",
    Icon: CrownIcon,
    hasGradientBorder: true,
    textClassName: "text-transparent bg-clip-text bg-linear-(--notification-gradient)",
    iconClassName: "size-2",
    iconTextTranslation: "ambassador_application_type",
    titleImage: true,
    titleHighlightParam: "first_name",
    titleHighlightClassName: "text-primary-dark-solid",
    actionUrl: params => (params.group_id ? `/group/${params.group_id}/` : `/company/${params.company_id}`),
  },
  PERMISSION_UPGRADE_ACCEPTED: {
    type: "PERMISSION_UPGRADE_ACCEPTED",
    hasGradientBorder: true,
    Icon: UsersGradientIcon,
    textClassName: "text-transparent bg-clip-text bg-linear-(--notification-gradient)",
    iconTextTranslation: "users_notification_type",
    onClick: params =>
      getDefaultStore().set(settingsModalAtom, {scope: {id: params.company_id, type: "brand"}, section: "users"}),
  },
  PERMISSION_UPGRADE_REJECTED: {
    type: "PERMISSION_UPGRADE_REJECTED",
    hasGradientBorder: true,
    Icon: UsersGradientIcon,
    textClassName: "text-transparent bg-clip-text bg-linear-(--notification-gradient)",
    iconTextTranslation: "users_notification_type",
    onClick: params =>
      getDefaultStore().set(settingsModalAtom, {scope: {id: params.company_id, type: "brand"}, section: "users"}),
  },
  PERMISSION_UPGRADE_REQUEST: {
    type: "PERMISSION_UPGRADE_REQUEST",
    hasGradientBorder: true,
    Icon: UsersGradientIcon,
    textClassName: "text-transparent bg-clip-text bg-linear-(--notification-gradient)",
    iconTextTranslation: "users_notification_type",
    onClick: params =>
      getDefaultStore().set(settingsModalAtom, {scope: {id: params.company_id, type: "brand"}, section: "users"}),
  },
  RATING: {
    type: "RATING",
    hasGradientBorder: true,
    Icon: StarIcon,
    textClassName: "text-transparent bg-clip-text bg-linear-(--notification-gradient)",
    iconTextTranslation: "rate_your_influencers_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=${params.on_going ? "active" : "applications"}`,
  },
  REFUND: {
    type: "REFUND",
    iconTextTranslation: "cash_reward_refund_type",
    textClassName: "bg-neutral-white-dark text-neutral-black",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=removed`,
  },
  REPORT: {
    type: "REPORT",
    Icon: ChartsIcon,
    textClassName: "text-primary-dark-solid",
    iconTextTranslation: "report_available_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=${params.on_going ? "active" : "applications"}`,
  },
  SEND_PACKAGES_REMINDER: {
    type: "SEND_PACKAGES_REMINDER",
    Icon: BoxArrowIcon,
    textClassName: "text-warning-dark-yellow",
    iconTextTranslation: "send_package_body_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=${params.on_going ? "preparation" : "overview"}`,
  },
  TIKTOK_VISIBILITY_DISCOVERY: {
    type: "TIKTOK_VISIBILITY_DISCOVERY",
    Icon: SearchIcon,
    textClassName: "text-primary-dark-solid",
    iconTextTranslation: "tiktok_search_type",
    actionUrl: params => {
      const {agencies, user_access_level} = useUserStore.getState().user;
      const agency = agencies?.[0]?.agency;
      const isAgency = user_access_level === "AGENCY" || !!agency?.multiple_brands;
      return isAgency && agency
        ? `/agency/${agency.id}/subbrand/${params.company_id}/search-visibility?tab=overview`
        : `/company/${params.company_id}/search-visibility?tab=overview`;
    },
    onClick: params =>
      getDefaultStore().set(tiktokVisibilitySelectedCountryAtom, prev => ({
        ...prev,
        [params.company_id]: params.region as RPNInput.Country,
      })),
  },
};

const useNotificationType = () => (type: TNotificationType) => NotificationType[type];

export default useNotificationType;
